// Flipping a player card. One delegated listener for the whole page rather than a component
// per card, because a club with sixty children would otherwise mount sixty islands to do
// nothing but toggle a class.
//
// The hidden face is taken out of the tab order and hidden from assistive tech, so a keyboard
// user never lands on invisible text. Without JavaScript the card simply shows both faces
// stacked, which is readable if unglamorous.
const faces = (card) => ({
  front: card.querySelector('.player-front'),
  back: card.querySelector('.player-back'),
});

function apply(card) {
  const flipped = card.classList.contains('is-flipped');
  const { front, back } = faces(card);
  for (const [face, hidden] of [
    [front, flipped],
    [back, !flipped],
  ]) {
    if (!face) continue;
    face.setAttribute('aria-hidden', String(hidden));
    for (const node of face.querySelectorAll('button, input, a'))
      if (hidden) node.setAttribute('tabindex', '-1');
      else node.removeAttribute('tabindex');
  }
}

// Cards that arrive after first paint — the one in the card dialog — need the same per-card
// setup, but must not bind a second delegated listener. This does the setup only.
export function prepareCards(root = document) {
  for (const card of root.querySelectorAll('.player-card')) {
    card.classList.add('is-interactive');
    apply(card);
  }
}

export function initCardFlip(root = document) {
  prepareCards(root);

  root.addEventListener('click', (event) => {
    // The whole card flips, not just the corner button, because that is what a card does when
    // you pick it up. Anything genuinely interactive on the face keeps its own behaviour: the
    // select checkbox must tick, not flip, or bulk approval becomes impossible to use.
    if (event.target.closest('input, label, a, select, textarea')) return;
    const card = event.target.closest('.player-card');
    if (!card) return;
    card.classList.toggle('is-flipped');
    apply(card);
    // Move focus to the face now showing, so a keyboard user is not left on a hidden button.
    const next =
      card.querySelector('.player-card.is-flipped .player-back [data-flip]') ||
      card.querySelector(
        card.classList.contains('is-flipped')
          ? '.player-back [data-flip]'
          : '.player-front [data-flip]'
      );
    next?.focus();
  });
}
